import Meta from './Meta'
import store from './store/index'
import { IEducationModule } from '@/contracts/modules/education/IEducationModule'
import { IModuleMeta } from '@/contracts/core/module/interface/IModuleMeta'
import { IStore } from '@/contracts/modules/education/stores/IStore'
import { IPack, IPackQuestion } from '@/contracts/modules/education/entities/IPack'
import { IQuestion } from '@/contracts/modules/education/entities/IQuestion'
import { ISettings } from '@/contracts/modules/education/entities/ISettings'
import { IQuestionData } from '@/contracts/modules/education/entities/IQuestionData'
import { IAnswered, IPackRecord } from '@/contracts/modules/education/entities/IPackRecord'
import { EnumWeight, isWeight } from '@/contracts/modules/education/enum/EnumWeight'
import { EnumEducationError } from '@/contracts/modules/education/enum/EnumEducationError'
import { IQuestionCipher } from '@/contracts/modules/education/cipher/IQuestionCipher'
import { IDecryptQuestion } from '@/contracts/modules/education/use-case/IDecryptQuestion'
import { IGetPack } from '@/contracts/modules/education/use-case/IGetPack'
import { ISaveAnswer } from '@/contracts/modules/education/use-case/ISaveAnswer'
import { ICreateQuestion } from '@/contracts/modules/education/use-case/ICreateQuestion'
import { IChangeQuestion } from '@/contracts/modules/education/use-case/IChangeQuestion'
import { IRemoveQuestion } from '@/contracts/modules/education/use-case/IRemoveQuestion'
import { IRestoreQuestion } from '@/contracts/modules/education/use-case/IRestoreQuestion'
import { IGetQuestions } from '@/contracts/modules/education/use-case/IGetQuestions'
import { IGetSettings } from '@/contracts/modules/education/use-case/IGetSettings'
import { IChangeSettings } from '@/contracts/modules/education/use-case/IChangeSettings'
import { ICheckConnection } from '@/contracts/modules/education/use-case/ICheckConnection'
import { IUserKey } from '@/contracts/system-modules/auth/key/IUserKey'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { sys } from '@/system/SystemProvider'
import { QuestionCipher } from '@/modules/education/v1/cipher/QuestionCipher'
import { Question } from '@/modules/education/v1/entities/Question'
import { Pack, PackQuestion } from '@/modules/education/v1/entities/Pack'
import { DecryptQuestion } from '@/modules/education/v1/use-case/DecryptQuestion'
import { GetPack } from '@/modules/education/v1/use-case/GetPack'
import { SaveAnswer } from '@/modules/education/v1/use-case/SaveAnswer'
import { CreateQuestion } from '@/modules/education/v1/use-case/CreateQuestion'
import { ChangeQuestion } from '@/modules/education/v1/use-case/ChangeQuestion'
import { RemoveQuestion } from '@/modules/education/v1/use-case/RemoveQuestion'
import { RestoreQuestion } from '@/modules/education/v1/use-case/RestoreQuestion'
import { GetQuestions } from '@/modules/education/v1/use-case/GetQuestions'
import { GetSettings } from '@/modules/education/v1/use-case/GetSettings'
import { ChangeSettings } from '@/modules/education/v1/use-case/ChangeSettings'
import { CheckConnection, isConnectionLost } from '@/modules/education/v1/use-case/CheckConnection'

/** Текст вместо вопроса, ключ или данные которого не расшифровались */
export const UNREADABLE_TEXT = 'Не удалось расшифровать'
/** Пауза повтора проверки связи и период опроса пачки; тесты уменьшают их */
export const educationSettings = { connectionCheckMs: 10000, packPollMs: 60000 }
/** Код ответа узла: ответ на этот вопрос пачки уже записан (с другого устройства) */
const ALREADY_ANSWERED = 'already_answered'
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function compareTime(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

class EducationModuleV1 implements IEducationModule {
  private static instance: EducationModuleV1
  private store: IStore = store()
  private opened = false
  private checkTimer: ReturnType<typeof setTimeout> | null = null
  private pollTimer: ReturnType<typeof setInterval> | null = null
  /** Момент показа текущего вопроса: из него считается время на ответ */
  private shownAt = 0
  /** Последняя пачка с узла и расшифрованные данные её вопросов (null — нечитаемый) */
  private packRecord: IPackRecord | null = null
  private packData = new Map<string, IQuestionData | null>()
  private cipher: IQuestionCipher = new QuestionCipher()
  private decryptQuestion: IDecryptQuestion = new DecryptQuestion()
  private getPackUseCase: IGetPack = new GetPack()
  private saveAnswerUseCase: ISaveAnswer = new SaveAnswer()
  private createQuestionUseCase: ICreateQuestion = new CreateQuestion()
  private changeQuestionUseCase: IChangeQuestion = new ChangeQuestion()
  private removeQuestionUseCase: IRemoveQuestion = new RemoveQuestion()
  private restoreQuestionUseCase: IRestoreQuestion = new RestoreQuestion()
  private getQuestionsUseCase: IGetQuestions = new GetQuestions()
  private getSettingsUseCase: IGetSettings = new GetSettings()
  private changeSettingsUseCase: IChangeSettings = new ChangeSettings()
  private checkConnectionUseCase: ICheckConnection = new CheckConnection()

  static getInstance(): EducationModuleV1 {
    if (!EducationModuleV1.instance) {
      EducationModuleV1.instance = new EducationModuleV1()
    }
    return EducationModuleV1.instance
  }

  health(): boolean {
    return true
  }

  init(): boolean {
    return true
  }

  destroy(): boolean {
    this.close()
    return true
  }

  getMeta(): IModuleMeta {
    return new Meta()
  }

  getViewPath(): string {
    return '/education/v1/view/index.vue'
  }

  async open(): Promise<void> {
    if (this.opened) {
      return
    }
    this.opened = true
    await this._checkConnection()
  }

  close(): void {
    this.opened = false
    this._stopCheck()
    this._stopPoll()
  }

  async reload(): Promise<void> {
    if (!this.opened || !this.store.connected) {
      return
    }
    await this._load()
  }

  hasUserKey(): boolean {
    return sys().auth.getUserKey() !== null
  }

  isConnected(): boolean {
    return this.store.connected
  }

  getError(): string | null {
    return this.store.error
  }

  clearError(): void {
    this.store.error = null
  }

  getPack(): IPack | null {
    return this.store.pack
  }

  getCurrentQuestion(): IPackQuestion | null {
    if (!this.hasUserKey() || this.store.pack === null) {
      return null
    }
    return this.store.pack.getQuestions().find((question) => !question.isUnreadable() && question.getAnswered() === null) ?? null
  }

  getProgress(): { answered: number, total: number } {
    const readable = (this.store.pack?.getQuestions() ?? []).filter((question) => !question.isUnreadable())
    return { answered: readable.filter((question) => question.getAnswered() !== null).length, total: readable.length }
  }

  markShown(): void {
    this.shownAt = Date.now()
    this.store.isAnswerShown = false
  }

  isAnswerShown(): boolean {
    return this.store.isAnswerShown
  }

  showAnswer(): void {
    if (this.getCurrentQuestion() !== null) {
      this.store.isAnswerShown = true
    }
  }

  async answer(result: boolean): Promise<void> {
    const pack = this.store.pack
    const current = this.getCurrentQuestion()
    if (pack === null || current === null) {
      this.store.error = EnumEducationError.NO_CURRENT_QUESTION
      return
    }
    const ms = Math.max(0, Date.now() - this.shownAt)
    await this._run(async () => {
      try {
        const record = await this.saveAnswerUseCase.exec(pack.getUuid(), current.getUuid(), result, ms)
        this._markAnswered(current.getUuid(), { result: record.result, ms: record.ms })
      } catch (error) {
        if ((error as Error).message !== ALREADY_ANSWERED) {
          throw error
        }
        // ответ уже записан с другого устройства: отметки берутся с узла
        await this._loadPack()
      }
      this.store.isAnswerShown = false
    })
  }

  getQuestions(removed: boolean): IQuestion[] {
    if (!this.hasUserKey()) {
      return []
    }
    return this.store.questions
      .filter((question) => question.isRemoved() === removed)
      .sort((a, b) => compareTime(b.getCreatedAt(), a.getCreatedAt()))
  }

  async create(node: string, weight: EnumWeight, question: string, answer: string): Promise<string | null> {
    const userKey = this._userKey()
    if (userKey === null || !this._isInputValid(node, weight)) {
      return null
    }
    return this._run(async () => {
      const key = await this.cipher.createKey()
      const wrapped = await userKey.wrap(key)
      const data = await this.cipher.encrypt(key, { question, answer })
      const uuid = await this.createQuestionUseCase.exec(node.trim(), weight, wrapped, data)
      await this._loadQuestions()
      return uuid
    })
  }

  async change(uuid: string, weight: EnumWeight, question: string, answer: string): Promise<void> {
    const userKey = this._userKey()
    const stored = this.store.questions.find((item) => item.getUuid() === uuid)
    if (userKey === null || !isWeight(weight)) {
      return
    }
    if (stored === undefined) {
      this.store.error = EnumEducationError.QUESTION_NOT_FOUND
      return
    }
    if (stored.isUnreadable()) {
      this.store.error = EnumEducationError.CANNOT_DECRYPT
      return
    }
    await this._run(async () => {
      const key = await userKey.unwrap(stored.getKey())
      const data = await this.cipher.encrypt(key, { question, answer })
      await this.changeQuestionUseCase.exec(uuid, { weight, data })
      await Promise.all([this._loadQuestions(), this._loadPack()])
    })
  }

  async remove(uuid: string): Promise<void> {
    await this._run(async () => {
      await this.removeQuestionUseCase.exec(uuid)
      await Promise.all([this._loadQuestions(), this._loadPack()])
    })
  }

  async restore(uuid: string): Promise<void> {
    await this._run(async () => {
      await this.restoreQuestionUseCase.exec(uuid)
      await Promise.all([this._loadQuestions(), this._loadPack()])
    })
  }

  getSettings(): ISettings | null {
    return this.store.settings
  }

  async changeSettings(settings: ISettings): Promise<void> {
    await this._run(async () => {
      this.store.settings = await this.changeSettingsUseCase.exec({ period: settings.period, size: settings.size })
    })
  }

  /** Действие с сервером: ошибка связи закрывает окно плашкой, другая ошибка — в getError(); результат — null при ошибке */
  private async _run<T>(action: () => Promise<T>): Promise<T | null> {
    try {
      const result = await action()
      this.store.error = null
      return result
    } catch (error) {
      const code = error instanceof Error ? error.message : String(error)
      if (isConnectionLost(code)) {
        this._lostConnection()
      } else {
        this.store.error = code
      }
      return null
    }
  }

  private _userKey(): IUserKey | null {
    const userKey = sys().auth.getUserKey()
    if (userKey === null) {
      this.store.error = EnumEducationError.NO_USER_KEY
    }
    return userKey
  }

  private _isInputValid(node: string, weight: number): boolean {
    if (!UUID_PATTERN.test(node.trim()) || !isWeight(weight)) {
      this.store.error = 'validation_failed'
      return false
    }
    return true
  }

  private async _checkConnection(): Promise<void> {
    if (!this.opened) {
      return
    }
    const connected = await this.checkConnectionUseCase.exec()
    if (!this.opened) {
      return
    }
    if (!connected) {
      this._lostConnection()
      return
    }
    if (!this.store.connected) {
      SysConsoleProviderModule().log('Education: связь с сервером восстановлена', EnumConsoleType.INFO)
    }
    this.store.connected = true
    await this._load()
    this._startPoll()
  }

  private _lostConnection(): void {
    if (this.store.connected) {
      SysConsoleProviderModule().log('Education: нет связи с сервером', EnumConsoleType.WARNING)
    }
    this.store.connected = false
    this._stopPoll()
    this._scheduleCheck()
  }

  private _scheduleCheck(): void {
    if (!this.opened || this.checkTimer !== null) {
      return
    }
    this.checkTimer = setTimeout(() => {
      this.checkTimer = null
      this._checkConnection()
    }, educationSettings.connectionCheckMs)
  }

  private _stopCheck(): void {
    if (this.checkTimer !== null) {
      clearTimeout(this.checkTimer)
      this.checkTimer = null
    }
  }

  /** Пока окно открыто и пачки нет или она пройдена — GET pack раз в период опроса */
  private _startPoll(): void {
    if (!this.opened || !this.store.connected || this.pollTimer !== null) {
      return
    }
    this.pollTimer = setInterval(() => {
      if (this.store.pack === null || this.getCurrentQuestion() === null) {
        this._run(() => this._loadPack())
      }
    }, educationSettings.packPollMs)
  }

  private _stopPoll(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  private async _load(): Promise<void> {
    this.store.hasUserKey = this.hasUserKey()
    await this._run(async () => {
      const [settings] = await Promise.all([this.getSettingsUseCase.exec(), this._loadPack(), this._loadQuestions()])
      this.store.settings = settings
    })
  }

  private async _loadPack(): Promise<void> {
    const record = await this.getPackUseCase.exec()
    const data = new Map<string, IQuestionData | null>()
    for (const question of record.questions) {
      data.set(question.uuid, await this._decrypt(question.key, question.data))
    }
    this.packRecord = record
    this.packData = data
    this.store.pack = this._buildPack()
  }

  private async _loadQuestions(): Promise<void> {
    const records = await this.getQuestionsUseCase.exec()
    const questions: IQuestion[] = []
    for (const record of records) {
      questions.push(new Question(record, await this._decrypt(record.key, record.data)))
    }
    this.store.questions = questions
  }

  /** Без ключа пользователя данные не расшифровываются: вопросы нечитаемы, пока ключ не появится */
  private async _decrypt(key: string, data: string): Promise<IQuestionData | null> {
    const userKey = sys().auth.getUserKey()
    if (userKey === null) {
      return null
    }
    return this.decryptQuestion.exec(key, data, userKey)
  }

  private _buildPack(): IPack | null {
    if (this.packRecord === null || this.packRecord.pack === null) {
      return null
    }
    return new Pack(this.packRecord.pack, this.packRecord.questions.map((question) => new PackQuestion(question, this.packData.get(question.uuid) ?? null)))
  }

  private _markAnswered(uuid: string, answered: IAnswered): void {
    if (this.packRecord === null) {
      return
    }
    this.packRecord = {
      pack: this.packRecord.pack,
      questions: this.packRecord.questions.map((question) => (question.uuid === uuid ? { ...question, answered } : question)),
    }
    this.store.pack = this._buildPack()
  }
}

export default function (): IEducationModule {
  return EducationModuleV1.getInstance()
}
